// WorkflowPerformerRoles.tsx

import { useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Plus, Pencil, Trash2 } from "lucide-react";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { deleteWorkflowRole, toggleWorkflowStatus, useWorkflowPerformerRoles } from "@/hooks/useLocalStorage";
import { WorkflowAction, WorkflowTeamsAction } from "@/lib/api-services";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Input } from "../ui/input";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { AddWorkflowPerformerRole } from "./AddWorkflowPerformerRole";

interface TableHierarchicalNodeProps {
    topLevelData: WorkflowTeamsAction[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onEditForm: (data: any) => void;
    onAlert: (message: string, status: boolean) => void; // lifted up
}

function TableForm({
    topLevelData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
    onEditForm,
    onAlert,
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<WorkflowTeamsAction[]>([]);

    const [showPopup, setShowPopup] = React.useState(false);
    const [popupName, setPopupName] = React.useState("");
    const [headingData, setHeadingData] = React.useState<string | null>("");
    const [descriptionContent, setDescriptionContent] = React.useState<string | null>("");

    const [_location, setLocation] = useLocation();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<WorkflowTeamsAction | null>(null);

    const [toggleMode, setToggleMode] = useState<"checkinout" | "activate">("checkinout");
    const [checkInOutDialogOpen, setCheckInOutDialogOpen] = useState(false);
    const [itemToCheckInOut, setItemToCheckInOut] = useState<WorkflowAction | null>(null);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [itemToToggle, setItemToToggle] = useState<WorkflowAction | null>(null);

    const deletePortalRole = deleteWorkflowRole();
    const togglePublishMutation = toggleWorkflowStatus();

    React.useEffect(() => {
        if (topLevelData) setCurrentData(topLevelData);
    }, [topLevelData]);

    function handleDeleteClick(e: React.MouseEvent, item: WorkflowTeamsAction) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        deletePortalRole.mutate(itemToDelete.id);
    }

    React.useEffect(() => {
        if (deletePortalRole.isSuccess) {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            onAlert("Workflow deleted successfully!", true);
            deletePortalRole.reset();
        } else if (deletePortalRole.isError) {
            const raw = deletePortalRole.error as Error;
            setDeleteDialogOpen(false);
            onAlert(parseApiErrorMessage(raw.message), false);
            deletePortalRole.reset();
        }
    }, [deletePortalRole.isSuccess, deletePortalRole.isError]);

    function handleConfirmToggle() {
        if (!itemToToggle) return;
        togglePublishMutation.mutate({
            Id: itemToToggle.PMId,
            data: { mode: toggleMode } as any,
        });
    }

    React.useEffect(() => {
        if (togglePublishMutation.isSuccess) {
            setCheckInOutDialogOpen(false);
            setApproveDialogOpen(false);
            const msg =
                toggleMode === "checkinout"
                    ? itemToToggle?.Status === 2 ? "Workflow Checked In successfully!" : "Workflow Checked Out successfully!"
                    : itemToToggle?.Active ? "Workflow Deactivated successfully!" : "Workflow Activated successfully!";
            setItemToCheckInOut(null);
            setItemToToggle(null);
            onAlert(msg, true);
            togglePublishMutation.reset();
        } else if (togglePublishMutation.isError) {
            const raw = togglePublishMutation.error as Error;
            setCheckInOutDialogOpen(false);
            setApproveDialogOpen(false);
            onAlert(parseApiErrorMessage(raw.message), false);
            togglePublishMutation.reset();
        }
    }, [togglePublishMutation.isSuccess, togglePublishMutation.isError]);

    function handleViewDescription(item: WorkflowTeamsAction) {
        setPopupName("Description");
        setHeadingData(item.roleName);
        setDescriptionContent(item.description);
        setShowPopup(true);
    }

    const confirmHeading =
        toggleMode === "checkinout"
            ? itemToToggle?.Status === 2 ? "Confirm Check In" : "Confirm Check Out"
            : itemToToggle?.Active ? "Confirm Deactivate" : "Confirm Activate";

    const confirmMessage =
        toggleMode === "checkinout"
            ? `Are you sure you want to ${itemToToggle?.Status === 2 ? "Check In" : "Check Out"} this workflow?`
            : `Are you sure you want to ${itemToToggle?.Active ? "Deactivate" : "Activate"} this workflow?`;

    const confirmLabel =
        toggleMode === "checkinout"
            ? itemToToggle?.Status === 2 ? "Check In" : "Check Out"
            : itemToToggle?.Active ? "Deactivate" : "Activate";

    return (
        <div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Id</TableHead>
                            <TableHead>Role Type</TableHead>
                            <TableHead>Role Name</TableHead>
                            <TableHead>Total Entry</TableHead>
                            <TableHead>Decrypt Data</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Created Time</TableHead>
                            <TableHead>Last Modified</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={data.id}>
                                <TableCell className="font-mono">{data.id}</TableCell>
                                <TableCell className="font-mono">{data.roleTypeName}</TableCell>
                                <TableCell className="font-mono">{data.roleName}</TableCell>
                                <TableCell className="font-mono">
                                    {data.userPermission ? data.totalEntry : "Unrestricted"}
                                </TableCell>
                                <TableCell className="font-mono">
                                    {data.decryptData ? (
                                        <Check className="h-4 w-4 mr-1 text-green-800" />
                                    ) : (
                                        <X className="h-4 w-4 mr-1 text-red-800" />
                                    )}
                                </TableCell>
                                <TableCell className="max-w-xs truncate">
                                    <Button variant="link" onClick={() => handleViewDescription(data)}>
                                        View
                                    </Button>
                                </TableCell>
                                <TableCell className="max-w-xs">
                                    <div className="flex flex-col">
                                        <span>
                                            {new Date(data.dateTimeAdded).toLocaleDateString("en-IN", {
                                                day: "2-digit", month: "short", year: "numeric",
                                            })}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {new Date(data.dateTimeAdded).toLocaleTimeString("en-IN", {
                                                hour: "2-digit", minute: "2-digit",
                                            })}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="max-w-xs">
                                    <div className="flex flex-col">
                                        {data.lastModified ? (
                                            <>
                                                <span>
                                                    {new Date(data.lastModified).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {new Date(data.lastModified).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-row items-center space-x-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="min-w-6 h-6 p-0" tooltip="More actions">
                                                    <Plus className="h-3 w-3" />
                                                    <span className="sr-only">More actions</span>
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[180px]">
                                                <DropdownMenuItem
                                                    className="flex items-center px-1 cursor-pointer"
                                                    onClick={() => onEditForm(data)}
                                                >
                                                    <Pencil className="h-4 w-4 mr-1" />
                                                    <span className="text-sm">Edit Form</span>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={(e) => handleDeleteClick(e, data)}
                                                    className="flex items-center px-1 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                                                >
                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                    <span className="text-sm">Delete</span>
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={headingData}
                    content={descriptionContent}
                    onClose={() => setShowPopup(false)}
                />
            )}

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={deletePortalRole.isPending}
                itemName={itemToDelete as any}
                itemType="Workflow"
                requireAuthCode={false}
            />

            <ConfirmationDialog
                open={approveDialogOpen}
                onOpenChange={setApproveDialogOpen}
                onConfirm={handleConfirmToggle}
                isPending={togglePublishMutation.isPending}
                heading={confirmHeading}
                message={confirmMessage}
                confirmLabel={confirmLabel}
            />
        </div>
    );
}


export function WorkflowPerformerRoles() {
    const { setBreadcrumbs } = useBreadcrumbs();

    const [topLevelTypes, setTopLevelTypes] = useState<WorkflowTeamsAction[]>([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [filterOpen, setFilterOpen] = useState(false);

    const [wfIdInput, setWfIdInput] = useState<string>("");
    const [wfNameInput, setWfNameInput] = useState<string>("");
    const [wfStatusInput, setWfStatusInput] = useState<string>("");

    const [appliedWfId, setAppliedWfId] = useState<string | undefined>();
    const [appliedWfName, setAppliedWfName] = useState<string | undefined>();
    const [appliedWfStatus, setAppliedWfStatus] = useState<string | undefined>();

    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const { data: types, isLoading } = useWorkflowPerformerRoles(
        pageIndex,
        pageSize,
        appliedWfId,
        appliedWfName,
        appliedWfStatus
    );

    React.useEffect(() => {
        setBreadcrumbs([{ label: "Performer Teams", path: `/workflowPerformer` }]);
    }, []);

    React.useEffect(() => {
        setTopLevelTypes(types?.items ?? []);
        setTotalCount(types?.total ?? 0);
    }, [types]);

    const handleSearch = () => {
        setAppliedWfId(wfIdInput.trim() || undefined);
        setAppliedWfName(wfNameInput.trim() || undefined);
        setAppliedWfStatus(wfStatusInput || undefined);
        setPageIndex(1);
    };

    const handleRefresh = (close: boolean) => {
        setWfIdInput("");
        setWfNameInput("");
        setWfStatusInput("");
        setAppliedWfId(undefined);
        setAppliedWfName(undefined);
        setAppliedWfStatus(undefined);
        setPageIndex(1);
        if (close) setFilterOpen(false);
    };

    const handleAddForm = () => {
        setEditFormData(null);
        setOpenCreateDialog(true);
    };

    const handleEditForm = (data: any) => {
        setEditFormData(data);
        setOpenCreateDialog(true);
    };

    const handleAlert = (message: string, status: boolean) => {
        setAlertMessage(message);
        setReqStatus(status);
        setAlertOpen(true);
    };

    return (
        <div className="bg-gray-50 min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                            Manage Performer Roles
                        </h2>
                        <p className="text-gray-600 mt-1 text-sm md:text-base">
                            Add or modify workflow performer roles
                        </p>
                    </div>
                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen((prev) => !prev)}
                        >
                            Filter Performer
                        </Button>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={handleAddForm}
                        >
                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                            Add Performer Roles
                        </Button>
                    </div>
                </div>
            </div>

            {filterOpen && (
                <Card className="mb-4 md:mb-6">
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">Id</label>
                                <Input
                                    placeholder="Enter Id"
                                    value={wfIdInput}
                                    onChange={(e) => setWfIdInput(e.target.value)}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">Role Name</label>
                                <Input
                                    placeholder="Enter Role Name"
                                    value={wfNameInput}
                                    onChange={(e) => setWfNameInput(e.target.value)}
                                />
                            </div>

                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">User Permission</label>
                                <Select
                                    value={wfStatusInput}
                                    onValueChange={(value) => setWfStatusInput(value === "all" ? "" : value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Permission" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        <SelectItem value="true">Restricted</SelectItem>
                                        <SelectItem value="false">Unrestricted</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-center space-x-2">
                            <Button onClick={handleSearch}>Search</Button>
                            <Button variant="outline" onClick={() => handleRefresh(false)}>
                                Refresh
                            </Button>
                            <Button variant="outline" onClick={() => handleRefresh(true)}>
                                Close
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {!isLoading ? (
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <div className="space-y-2">
                            {topLevelTypes && topLevelTypes.length > 0 ? (
                                <TableForm
                                    topLevelData={topLevelTypes}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                    onEditForm={handleEditForm}
                                    onAlert={handleAlert}
                                />
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No Workflow found. Create one to get started.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="p-6">Loading...</div>
            )}

            {openCreateDialog && (
                <AddWorkflowPerformerRole
                    open={openCreateDialog}
                    onOpenChange={setOpenCreateDialog}
                    initialData={editFormData}
                    onAlert={handleAlert}
                />
            )}

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}